import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { isHomolog } from "@/utils/environment";
import { useRecipientColumns } from "./columns";
import { CronTestPanel } from "./components/cron-test-panel";
import { DataTable } from "./components/data-table";
import { DataTablePagination } from "./components/data-table-pagination";
import { DataTableToolbar } from "./components/data-table-toolbar";
import { RecipientCards } from "./components/recipient-cards";
import { useSectorEmailsFilterActions } from "./filter/hooks/actions";
import { useSectorEmailsFilters } from "./filter/hooks/filterState";
import { AppliedSectorEmailsFilters } from "./filter/types";
import { toRecipientSorting, toSortingState } from "./sorting";
import { useRecipientsExport } from "./hooks/use-recipients-export";
import { useRecipientsSource } from "./hooks/use-recipients-source";
import { SECTOR_EMAILS_LABELS } from "./messages";

const countSelectedFilters = (
  applied: AppliedSectorEmailsFilters
): number =>
  applied.active.length +
  applied.sectors.length +
  applied.shipping_types.length;

const SectorEmails = (): JSX.Element => {
  const filters = useSectorEmailsFilters();
  const { rows, meta, total, actions } = useRecipientsSource(filters);
  const { exportRecipients, isExporting } = useRecipientsExport(filters);
  const columns = useRecipientColumns({ actions });
  const { setSorting } = useSectorEmailsFilterActions();

  const sortingState: SortingState = toSortingState(filters.sorting);

  const handleSortingChange: OnChangeFn<SortingState> = (updater): void => {
    const next: SortingState =
      typeof updater === "function" ? updater(sortingState) : updater;

    setSorting(toRecipientSorting(next));
  };

  const applied: AppliedSectorEmailsFilters = {
    active: filters.active,
    sectors: filters.sectors,
    shipping_types: filters.shipping_types,
  };

  return (
    <PageLayout>
      <PageTitle
        title={SECTOR_EMAILS_LABELS.PAGE_TITLE}
        description={SECTOR_EMAILS_LABELS.PAGE_DESCRIPTION}
      />

      <ContentContainer className="space-y-4">
        {isHomolog() && <CronTestPanel />}

        <DataTableToolbar
          applied={applied}
          activeFiltersCount={countSelectedFilters(applied)}
          filteredTotal={meta.total}
          total={total}
          onExport={exportRecipients}
          isExporting={isExporting}
          onCreate={actions.create}
        />

        <DataTable
          columns={columns}
          data={rows}
          sorting={sortingState}
          onSortingChange={handleSortingChange}
        />
        <RecipientCards recipients={rows} actions={actions} />

        <DataTablePagination meta={meta} />
      </ContentContainer>
    </PageLayout>
  );
};

export default SectorEmails;
